import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { getLoggedInUser } from '../../utils/permissions';
import { fetchClaimsReportingData, getClaimDate, normalizeAssignedLocations } from '../../utils/claimsReporting';

const STATUSES = [
  ['reported', 'Reported', '#2878d0'],
  ['processing', 'Processing', '#ef9b28'],
  ['pendingClose', 'Pending Close', '#8b5cf6'],
  ['closed', 'Closed', '#28a56c'],
];
const PERIODS = [
  ['7', 'Last 7 days'],
  ['30', 'Last 30 days'],
  ['90', 'Last 3 months'],
  ['365', 'Last 12 months'],
  ['custom', 'Custom date range'],
  ['all', 'All time'],
];

const firstVehicle = (row) => (
  (Array.isArray(row?.details_by_vehicle) && row.details_by_vehicle[0])
  || (Array.isArray(row?.vehicle_details) && row.vehicle_details[0])
  || row?.vehicle
  || {}
);
const valueOf = (...values) => values.find((value) => String(value || '').trim()) || '';
const employeeOf = (row, type) => type === 'created'
  ? valueOf(row.created_by, row.reportingEmployeeName, row.submitter_name, row.createdBy, 'Unknown')
  : valueOf(row.updated_by, row.modified_by, row.closed_by, row.last_modified_by, 'Not modified');
const vehicleOf = (row) => {
  const vehicle = firstVehicle(row);
  return {
    vin: valueOf(vehicle.vin, row.vin, row.vehicle_vin),
    unit: valueOf(vehicle.unitFleetNumber, row.unitFleetNumber, row.unit, row.unit_number),
    make: valueOf(vehicle.make, row.make),
    model: valueOf(vehicle.model, row.model),
    color: valueOf(vehicle.color, row.color),
  };
};
const grouped = (rows, getKey, getMeta = () => ({})) => {
  const result = new Map();
  rows.forEach((row) => {
    const key = String(getKey(row) || '').trim();
    if (!key) return;
    const current = result.get(key) || { name: key, count: 0, ...getMeta(row) };
    current.count += 1;
    result.set(key, current);
  });
  return [...result.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};
const dateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const formatDate = (value) => new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function Ranking({ title, rows, emptyText, color = '#2878d0' }) {
  const max = Math.max(1, ...rows.map((item) => item.count));
  return (
    <Paper sx={{ p: 3, borderRadius: 3, minHeight: 260 }}>
      <Typography variant="h6" fontWeight={800}>{title}</Typography>
      {!rows.length && <Typography color="text.secondary" sx={{ mt: 3 }}>{emptyText}</Typography>}
      {rows.slice(0, 10).map((item) => (
        <Box key={item.name} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography fontSize={13} fontWeight={700} noWrap title={item.name}>{item.name}</Typography>
            <Typography fontSize={13} fontWeight={800}>{item.count}</Typography>
          </Box>
          <Box sx={{ mt: .6, height: 8, borderRadius: 5, bgcolor: '#edf1f6' }}>
            <Box sx={{ width: `${item.count / max * 100}%`, height: '100%', borderRadius: 5, bgcolor: color }} />
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

export default function InsightsPage() {
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => getLoggedInUser(), []);
  const assigned = useMemo(
    () => normalizeAssignedLocations(user?.selected_locations || user?.locations).map((item) => item.toLowerCase()),
    [user],
  );
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('30');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [location, setLocation] = useState('all');
  const [module, setModule] = useState('all');

  useEffect(() => {
    fetchClaimsReportingData(baseURL)
      .then(setRecords)
      .catch(() => setError('Insights data could not be loaded.'))
      .finally(() => setLoading(false));
  }, [baseURL]);

  const locationRecords = useMemo(() => records.filter(
    (row) => assigned.includes(String(row.reportingLocation).toLowerCase()),
  ), [assigned, records]);
  const locations = useMemo(
    () => [...new Set(locationRecords.map((row) => row.reportingLocation))].sort(),
    [locationRecords],
  );
  const dateBounds = useMemo(() => {
    if (period === 'custom' || period === 'all') {
      return {
        from: period === 'custom' && fromDate ? new Date(`${fromDate}T00:00:00`) : null,
        to: period === 'custom' && toDate ? new Date(`${toDate}T23:59:59.999`) : null,
      };
    }
    const to = new Date();
    to.setHours(23, 59, 59, 999);
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    from.setDate(from.getDate() - Number(period) + 1);
    return { from, to };
  }, [fromDate, period, toDate]);
  const visible = useMemo(() => locationRecords.filter((row) => {
    const date = getClaimDate(row);
    return (location === 'all' || row.reportingLocation === location)
      && (module === 'all' || row.reportingWorkflow === module)
      && (!dateBounds.from || (date && date >= dateBounds.from))
      && (!dateBounds.to || (date && date <= dateBounds.to));
  }), [dateBounds, location, locationRecords, module]);

  const byLocation = useMemo(() => grouped(visible, (row) => row.reportingLocation), [visible]);
  const createdBy = useMemo(() => grouped(visible, (row) => employeeOf(row, 'created')), [visible]);
  const modifiedBy = useMemo(
    () => grouped(visible.filter((row) => employeeOf(row, 'modified') !== 'Not modified'), (row) => employeeOf(row, 'modified')),
    [visible],
  );
  const vehicles = useMemo(() => grouped(
    visible,
    (row) => vehicleOf(row).vin,
    (row) => {
      const vehicle = vehicleOf(row);
      return { unit: vehicle.unit, description: [vehicle.make, vehicle.model, vehicle.color].filter(Boolean).join(' / ') };
    },
  ), [visible]);
  const withVin = visible.filter((row) => vehicleOf(row).vin).length;
  const uniqueVins = new Set(visible.map((row) => vehicleOf(row).vin).filter(Boolean)).size;
  const repeatVehicles = vehicles.filter((row) => row.count > 1).length;
  const daily = useMemo(() => grouped(visible, (row) => {
    const date = getClaimDate(row);
    return date ? dateKey(date) : '';
  }).sort((a, b) => a.name.localeCompare(b.name)), [visible]);
  const maxDaily = Math.max(1, ...daily.map((item) => item.count));

  return (
    <Box sx={{ ml: { md: '240px' }, mt: '64px', p: { xs: 2, md: 4 }, minHeight: 'calc(100vh - 64px)', bgcolor: '#f5f7fb' }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" fontWeight={800} color="#123b76">Claims Insights</Typography>
            <Typography color="text.secondary">Vehicle, employee, date, status, and location trends for your assigned locations.</Typography>
          </Box>
          <Button variant="outlined" onClick={() => { setPeriod('30'); setFromDate(''); setToDate(''); setLocation('all'); setModule('all'); }}>Reset Filters</Button>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', lg: period === 'custom' ? 'repeat(5,1fr)' : 'repeat(3,1fr)' }, gap: 1.5, mt: 3 }}>
          <FormControl size="small"><InputLabel>Date period</InputLabel><Select label="Date period" value={period} onChange={(event) => setPeriod(event.target.value)}>{PERIODS.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</Select></FormControl>
          {period === 'custom' && <TextField type="date" label="From date" InputLabelProps={{ shrink: true }} size="small" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />}
          {period === 'custom' && <TextField type="date" label="To date" InputLabelProps={{ shrink: true }} size="small" value={toDate} onChange={(event) => setToDate(event.target.value)} />}
          <FormControl size="small"><InputLabel>Location</InputLabel><Select label="Location" value={location} onChange={(event) => setLocation(event.target.value)}><MenuItem value="all">All assigned locations</MenuItem>{locations.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}</Select></FormControl>
          <FormControl size="small"><InputLabel>Module</InputLabel><Select label="Module" value={module} onChange={(event) => setModule(event.target.value)}><MenuItem value="all">All claim modules</MenuItem>{['Vehicle Damage', 'Windshield / Tire', 'Shuttle Bus'].map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}</Select></FormControl>
        </Box>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Box py={10} textAlign="center"><CircularProgress /></Box> : <>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', xl: 'repeat(4,1fr)' }, gap: 2, mb: 2 }}>
          {STATUSES.map(([key, label, color]) => {
            const count = visible.filter((row) => row.reportingStage === key).length;
            return <Paper key={key} sx={{ p: 2.5, borderRadius: 3, borderLeft: `4px solid ${color}` }}><Typography color="text.secondary" fontWeight={700}>{label}</Typography><Typography fontSize={34} fontWeight={800}>{count}</Typography><Typography variant="caption" color="text.secondary">{visible.length ? Math.round(count / visible.length * 100) : 0}% of {visible.length} selected records</Typography></Paper>;
          })}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 2, mb: 2 }}>
          {[['Unique VINs', uniqueVins, `${withVin} records contain a VIN`], ['Repeat vehicles', repeatVehicles, 'VINs appearing in multiple claims'], ['Employee activity', new Set([...createdBy, ...modifiedBy].map((item) => item.name)).size, 'Creators and modifiers in selection']].map(([label, count, note]) => <Paper key={label} sx={{ p: 2.5, borderRadius: 3 }}><Typography color="text.secondary" fontWeight={700}>{label}</Typography><Typography fontSize={30} fontWeight={800} color="#123b76">{count}</Typography><Typography variant="caption" color="text.secondary">{note}</Typography></Paper>)}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2,1fr)' }, gap: 2, mb: 2 }}>
          <Ranking title="Records Created by Employee" rows={createdBy} emptyText="No employee information found." />
          <Ranking title="Records Modified by Employee" rows={modifiedBy} emptyText="No modification activity found." color="#8b5cf6" />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.3fr .7fr' }, gap: 2, mb: 2 }}>
          <Paper sx={{ p: 3, borderRadius: 3, overflow: 'hidden' }}>
            <Typography variant="h6" fontWeight={800}>Claims by Vehicle (VIN)</Typography>
            <TableContainer sx={{ mt: 1, maxHeight: 390 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell sx={{ fontWeight: 800 }}>VIN</TableCell><TableCell sx={{ fontWeight: 800 }}>Unit</TableCell><TableCell sx={{ fontWeight: 800 }}>Vehicle</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>Claims</TableCell></TableRow></TableHead>
                <TableBody>{vehicles.slice(0, 50).map((item) => <TableRow key={item.name} hover><TableCell>{item.name}</TableCell><TableCell>{item.unit || '-'}</TableCell><TableCell>{item.description || '-'}</TableCell><TableCell align="right"><strong>{item.count}</strong></TableCell></TableRow>)}</TableBody>
              </Table>
            </TableContainer>
            {!vehicles.length && <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No VIN information in this selection.</Typography>}
          </Paper>
          <Ranking title="Claims by Location" rows={byLocation} emptyText="No records for this period." color="#28a56c" />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.3fr .7fr' }, gap: 2 }}>
          <Paper sx={{ p: 3, borderRadius: 3, overflow: 'hidden' }}>
            <Typography variant="h6" fontWeight={800}>Date-wise Claim Activity</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: .8, height: 210, mt: 3, overflowX: 'auto', pb: 1 }}>
              {daily.slice(-60).map((item) => <Box key={item.name} title={`${formatDate(item.name)}: ${item.count}`} sx={{ minWidth: 18, flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}><Typography fontSize={10} fontWeight={700}>{item.count}</Typography><Box sx={{ width: '100%', maxWidth: 30, minHeight: 4, height: `${item.count / maxDaily * 160}px`, bgcolor: '#2878d0', borderRadius: '5px 5px 0 0' }} /></Box>)}
              {!daily.length && <Typography color="text.secondary" sx={{ alignSelf: 'center', width: '100%', textAlign: 'center' }}>No dated records in this selection.</Typography>}
            </Box>
          </Paper>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={800}>Claims by Module</Typography>
            {['Vehicle Damage', 'Windshield / Tire', 'Shuttle Bus'].map((workflow) => {
              const count = visible.filter((row) => row.reportingWorkflow === workflow).length;
              return <Box key={workflow} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.7, borderBottom: '1px solid #edf1f6' }}><Typography>{workflow}</Typography><Typography fontWeight={800}>{count}</Typography></Box>;
            })}
          </Paper>
        </Box>
      </>}
    </Box>
  );
}
